import { useCallback, useSyncExternalStore } from 'react';
import useDesktopScope from './useDesktopScope';

// Pending native work survives tab/component retirement, but never a renderer restart.
// No credentials or result payloads are retained here. Native owners retain recovery.
const claims = new WeakMap();
const listeners = new Set();
const subscribe = listener => { listeners.add(listener); return () => listeners.delete(listener); };
const notify = () => { for (const listener of listeners) listener(); };

export default function useDesktopCommand(group) {
  const scope = useDesktopScope();
  const lookup = useCallback(() => claims.get(scope.bridge)?.get(group) || null, [scope, group]);
  const claim = useSyncExternalStore(subscribe, lookup, () => null);
  const run = async (invoke, settle) => {
    if (!scope.current() || !scope.admin || !scope.bridge || lookup()) return false;
    let groups = claims.get(scope.bridge);
    if (!groups) { groups = new Map(); claims.set(scope.bridge, groups); }
    const token = { phase: 'pending' };
    groups.set(group, token);
    notify();
    let result;
    let failure;
    try { result = await invoke(scope.bridge); } catch (error) { failure = error; }
    if (!failure && (!result || typeof result !== 'object' || typeof result.state !== 'string')) {
      failure = new Error('Native command acknowledgement unavailable');
    }
    try {
      if (scope.current()) await settle?.(result, failure, scope.current);
    } finally {
      if (groups.get(group) === token) {
        if (failure) groups.set(group, { phase: 'unknown' });
        else groups.delete(group);
      }
      notify();
    }
    return true;
  };
  return { scope, busy: !!claim, uncertain: claim?.phase === 'unknown', run };
}
